import { Link, useParams } from "@/lib/nav";
import { useState } from "react";
import { AdminGate } from "@/components/AdminGate";
import { ChatModeration } from "@/components/ChatModeration";
import { LayoutEditor } from "@/components/LayoutEditor";
import { BoardHeader, BoardShell, Loading, NotFoundBoard } from "@/components/Chrome";
import { fmtDate, fmtTime } from "@/lib/format";
import {
  cancelAttendee,
  deleteInvite,
  editAttendee,
  importInvites,
  promoteStandby,
  setBoarded,
  setFlightStatus,
  setInviteOnly,
  setInviteQuota,
  useManifest,
} from "@/lib/store";
import type { Attendee, Flight } from "@/lib/types";
import { flightCapacity, parseInviteText, splitSeat } from "@/lib/types";

function seatCompare(a?: string, b?: string): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  const sa = splitSeat(a);
  const sb = splitSeat(b);
  const ta = parseInt(sa.table, 10) || 0;
  const tb = parseInt(sb.table, 10) || 0;
  return ta - tb || sa.letter.localeCompare(sb.letter);
}

function StatTile({ label, value, tone = "" }: { label: string; value: string; tone?: string }) {
  return (
    <div className="card px-4 py-3.5">
      <div className="text-sub text-[12px] font-medium">{label}</div>
      <div className={`mt-1 text-[20px] font-bold tabular-nums ${tone}`}>{value}</div>
    </div>
  );
}

/**
 * 名單上的內嵌改名表單（主辦人代改：報名時打錯字、用了別的稱呼）。
 *
 * 只改姓名與產業 —— 座位、登機證票號、接龍名額都不動：
 * 票號一換，對方手上那個連結就作廢了；名額是報名當下扣掉的，
 * 改個顯示名字不代表要還回去。
 *
 * 定義在模組層級而不是頁面元件裡面：名單每 4 秒輪詢一次，
 * 內嵌定義的話每次輪詢都會是「新的元件型別」，React 會整個重掛，
 * 打到一半的字就沒了。
 */
function EditName({ attendee, onDone }: { attendee: Attendee; onDone: () => void }) {
  const [name, setName] = useState(attendee.name);
  const [industry, setIndustry] = useState(attendee.industry ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  const save = async () => {
    if (busy || !name.trim() || !industry.trim()) return;
    setBusy(true);
    setError(false);
    const ok = await editAttendee(attendee.id, name.trim(), industry.trim());
    setBusy(false);
    if (!ok) {
      setError(true);
      return;
    }
    onDone();
  };

  const keys = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") void save();
    if (e.key === "Escape") onDone();
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={keys}
        maxLength={30}
        autoFocus
        aria-label="姓名"
        className="field-input w-28 px-2.5 py-1.5 text-[13px]"
      />
      <input
        value={industry}
        onChange={(e) => setIndustry(e.target.value)}
        onKeyDown={keys}
        maxLength={20}
        placeholder="產業"
        aria-label="產業"
        className="field-input w-24 px-2.5 py-1.5 text-[13px]"
      />
      <button
        onClick={save}
        disabled={busy || !name.trim() || !industry.trim()}
        className="btn btn-primary px-3 py-1.5 text-[12px] disabled:opacity-40"
      >
        {busy ? "…" : "儲存"}
      </button>
      {error && <span className="text-bad-deep text-[12px]">存不起來，再試一次</span>}
    </div>
  );
}

function exportCsv(flight: Flight, attendees: Attendee[]) {
  const esc = (v: string) => `"${v.replaceAll('"', '""')}"`;
  const rows = [
    ["姓名", "產業", "座位", "備註", "狀態", "已報到", "報到時間", "票號", "報名時間"],
    ...attendees.map((a) => [
      a.name,
      a.industry ?? "",
      a.seat ?? "",
      a.note ?? "",
      a.status === "confirmed" ? "確認" : "候補",
      a.checkedInAt ? "是" : "否",
      a.checkedInAt ? `${fmtDate(a.checkedInAt)} ${fmtTime(a.checkedInAt)}` : "",
      a.passToken,
      `${fmtDate(a.createdAt)} ${fmtTime(a.createdAt)}`,
    ]),
  ];
  // ﻿ = UTF-8 BOM，讓 Excel 正確認出中文
  const csv = "﻿" + rows.map((r) => r.map(esc).join(",")).join("\r\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `${flight.code}-passengers.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

/** 乘客名單（Manifest）：主辦人看報名狀況、管理名單 */
export default function ManifestPage() {
  const { code } = useParams<{ code: string }>();
  const [copied, setCopied] = useState(false);
  const [inviteText, setInviteText] = useState("");
  const [importMsg, setImportMsg] = useState<string | null>(null);

  return (
    <BoardShell wide>
      <BoardHeader sub="乘客名單" />
      <AdminGate>
        <ManifestBody
          code={code}
          copied={copied}
          setCopied={setCopied}
          inviteText={inviteText}
          setInviteText={setInviteText}
          importMsg={importMsg}
          setImportMsg={setImportMsg}
        />
      </AdminGate>
    </BoardShell>
  );
}

function ManifestBody({
  code,
  copied,
  setCopied,
  inviteText,
  setInviteText,
  importMsg,
  setImportMsg,
}: {
  code: string;
  copied: boolean;
  setCopied: (v: boolean) => void;
  inviteText: string;
  setInviteText: (v: string) => void;
  importMsg: string | null;
  setImportMsg: (v: string | null) => void;
}) {
  const view = useManifest(code);
  // 正在改哪一位的資料。宣告在早退之前 —— hook 的順序每次 render 都要一樣
  const [editing, setEditing] = useState<string | null>(null);
  if (view === undefined) return <Loading />;
  if (view === null) {
    return <NotFoundBoard message={`找不到活動 ${code}`} backHref="/admin" backLabel="返回後台" />;
  }

  const { flight, invites } = view;
  const confirmed = view.attendees
    .filter((a) => a.status === "confirmed")
    .sort((x, y) => seatCompare(x.seat, y.seat));
  const standby = view.attendees
    .filter((a) => a.status === "standby")
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const boarded = confirmed.filter((a) => a.checkedInAt).length;
  const cap = flightCapacity(flight);
  const hasSpace = confirmed.length < cap;

  const copySignup = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/flight/${flight.code}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };

  const row = (a: Attendee, isStandby: boolean) => (
    <div
      key={a.id}
      className="grid grid-cols-[52px_1fr_auto] items-center gap-3 border-t border-line px-4 py-3 first:border-t-0 sm:grid-cols-[60px_1.2fr_1fr_1fr_auto]"
    >
      <span className={`text-[15px] font-bold tabular-nums ${isStandby ? "text-warn-deep" : "text-accent"}`}>
        {a.seat ?? (isStandby ? "候補" : "—")}
      </span>
      <div className="min-w-0">
        {editing === a.id ? (
          <EditName attendee={a} onDone={() => setEditing(null)} />
        ) : (
          <>
            <div className="truncate text-[14px] font-semibold">
              {a.name}
              {a.industry && (
                <span className="text-sub ml-2 text-[12px] font-normal">{a.industry}</span>
              )}
            </div>
            {a.note && <div className="text-warn-deep truncate text-[12px] sm:hidden">{a.note}</div>}
          </>
        )}
      </div>
      <div className="hidden min-w-0 truncate text-[13px] sm:block">
        {a.note ? <span className="text-warn-deep">{a.note}</span> : <span className="text-sub">—</span>}
      </div>
      <div className="hidden text-[13px] sm:block">
        {a.checkedInAt ? (
          <span className="text-ok-deep">✓ 已報到 {fmtTime(a.checkedInAt)}</span>
        ) : isStandby ? (
          <span className="text-warn-deep">候補中</span>
        ) : (
          <span className="text-sub">未報到</span>
        )}
      </div>
      <div className="flex flex-wrap justify-end gap-1.5">
        {isStandby ? (
          <button
            onClick={() => promoteStandby(a.id)}
            disabled={!hasSpace}
            title={hasSpace ? "遞補為正取" : "目前沒有空位"}
            className="btn btn-secondary px-3 py-1.5 text-[12px]"
          >
            遞補
          </button>
        ) : (
          <button
            onClick={() => setBoarded(a.id, !a.checkedInAt)}
            className={`btn px-3 py-1.5 text-[12px] ${a.checkedInAt ? "btn-secondary" : "btn-primary"}`}
          >
            {a.checkedInAt ? "取消報到" : "報到"}
          </button>
        )}
        <button
          onClick={() => setEditing(editing === a.id ? null : a.id)}
          className="btn btn-secondary px-3 py-1.5 text-[12px]"
          title="改姓名與產業"
        >
          {editing === a.id ? "關閉" : "改"}
        </button>
        <Link href={`/pass/${a.passToken}`} target="_blank" className="btn btn-secondary px-3 py-1.5 text-[12px]">
          票
        </Link>
        <button
          onClick={() => {
            if (confirm(`確定取消 ${a.name} 的報名？`)) void cancelAttendee(a.id);
          }}
          className="btn btn-danger px-3 py-1.5 text-[12px]"
        >
          取消
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className="mb-5 flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
          <h1 className="text-[22px] font-bold tracking-tight">{flight.title}</h1>
          <span className="text-sub text-[13px]">
            {flight.code} · {fmtDate(flight.departAt)} {fmtTime(flight.departAt)} · {flight.venueName}
          </span>
        </div>
        <Link href="/admin" className="text-accent text-[13px] font-medium">
          ← 後台首頁
        </Link>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="正取" value={`${confirmed.length}/${cap}`} />
        <StatTile label="候補" value={String(standby.length)} tone={standby.length ? "text-warn-deep" : ""} />
        <StatTile
          label="已報到"
          value={`${boarded} (${confirmed.length ? Math.round((boarded / confirmed.length) * 100) : 0}%)`}
          tone="text-ok-deep"
        />
        <StatTile label="剩餘座位" value={String(Math.max(0, cap - confirmed.length))} />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={copySignup} className="btn btn-secondary px-3.5 py-2 text-[13px]">
          {copied ? "✓ 已複製" : "複製報名連結"}
        </button>
        <Link href={`/admin/${flight.code}/boarding`} className="btn btn-primary px-3.5 py-2 text-[13px]">
          登機口模式
        </Link>
        <button onClick={() => exportCsv(flight, [...confirmed, ...standby])} className="btn btn-secondary px-3.5 py-2 text-[13px]">
          匯出 CSV
        </button>
        <button
          onClick={() => setFlightStatus(flight.code, flight.status === "open" ? "closed" : "open")}
          className={`btn px-3.5 py-2 text-[13px] ${flight.status === "open" ? "btn-danger" : "btn-secondary"}`}
        >
          {flight.status === "open" ? "截止報名" : "重新開放"}
        </button>
        <button
          onClick={() => setInviteOnly(flight.code, !flight.inviteOnly)}
          className="btn btn-secondary px-3.5 py-2 text-[13px]"
        >
          接龍名單制：{flight.inviteOnly ? "開啟中 ✓" : "關閉中"}
        </button>
      </div>

      <details className="card mt-5 p-5">
        <summary className="cursor-pointer text-[16px] font-bold">
          桌邊聊天
          <span className="text-sub ml-2 text-[12px] font-normal">
            與會者的對話，可刪除不當內容
          </span>
        </summary>
        <ChatModeration code={flight.code} />
      </details>

      <details className="card mt-5 p-5">
        <summary className="cursor-pointer text-[16px] font-bold">
          場地平面配置
          <span className="text-sub ml-2 text-[12px] font-normal">
            拖曳桌子排出餐廳實際格局、改桌號
          </span>
        </summary>
        <div className="mt-4">
          <LayoutEditor code={flight.code} tables={flight.tables} />
        </div>
      </details>

      {flight.inviteOnly && (
        <div className="card mt-5 p-5">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-[16px] font-bold">接龍名單</h2>
            <span className="text-sub text-[12px]">
              只有名單上的名字能報名，且不能超過各自名額
            </span>
          </div>

          <textarea
            value={inviteText}
            onChange={(e) => setInviteText(e.target.value)}
            className="field-input mt-3 min-h-24 font-mono text-[13px]"
            placeholder={"把 LINE 接龍直接貼進來，例如：\n1. 王大明 +1\n2. 李小美 2位\n3. 陳大文"}
          />
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <button
              onClick={async () => {
                const entries = parseInviteText(inviteText);
                if (entries.length === 0) {
                  setImportMsg("看不懂這段文字，請一行一個名字。");
                  return;
                }
                const r = await importInvites(flight.code, entries);
                if (!r) {
                  setImportMsg("匯入失敗，請再試一次。");
                  return;
                }
                setImportMsg(`✓ 新增 ${r.added} 筆、更新 ${r.updated} 筆`);
                setInviteText("");
              }}
              className="btn btn-primary px-3.5 py-2 text-[13px]"
            >
              解析並匯入
            </button>
            {importMsg && <span className="text-sub text-[13px]">{importMsg}</span>}
          </div>

          {invites.length === 0 ? (
            <p className="text-warn-deep mt-4 text-[13px]">名單還是空的——現在沒有任何人能報名。</p>
          ) : (
            <div className="mt-4">
              <div className="text-sub mb-2 text-[12px]">
                共 {invites.length} 筆、總名額 {invites.reduce((n, i) => n + i.quota, 0)} 位（座位上限 {cap}）
              </div>
              <div className="grid grid-cols-1 gap-x-6 sm:grid-cols-2">
                {invites.map((inv) => (
                  <div key={inv.id} className="flex items-center justify-between gap-3 border-t border-line py-2">
                    <span className="min-w-0 truncate text-[14px] font-semibold">{inv.name}</span>
                    <span className="flex shrink-0 items-center gap-2 text-[13px]">
                      <span className={inv.used >= inv.quota ? "text-ok-deep" : "text-sub"}>
                        已用 {inv.used}/
                      </span>
                      <input
                        type="number"
                        min={1}
                        max={20}
                        defaultValue={inv.quota}
                        onChange={(e) => setInviteQuota(inv.id, Number(e.target.value) || 1)}
                        className="field-input w-16 px-2 py-1 text-center text-[13px]"
                      />
                      <button
                        onClick={() => {
                          if (confirm(`刪除「${inv.name}」的名額？（已報名的人不會被取消）`)) void deleteInvite(inv.id);
                        }}
                        className="btn btn-danger px-2.5 py-1 text-[12px]"
                      >
                        刪
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="card mt-5 overflow-hidden">
        <div className="text-sub border-b border-line px-4 py-2.5 text-[12px] font-medium">
          正取名單（依座位排序）
        </div>
        {confirmed.length === 0 ? (
          <div className="text-sub px-4 py-10 text-center text-[14px]">還沒有人報名</div>
        ) : (
          confirmed.map((a) => row(a, false))
        )}
      </div>

      {standby.length > 0 && (
        <div className="card mt-4 overflow-hidden">
          <div className="text-warn-deep border-b border-line px-4 py-2.5 text-[12px] font-medium">
            候補名單（依報名順序）
          </div>
          {standby.map((a) => row(a, true))}
        </div>
      )}
    </>
  );
}
